import React, { useState, useEffect, useMemo } from 'react';
import microbiologyService from '../../../services/microbiologyService';
import CommonPagination from '../../../components/common/CommonPagination';
import usePagination from '../../../hooks/usePagination';
import './MicrobiologyMasters.css';

export default function ASTPanelBuilderPage() {
  const [panels, setPanels] = useState([]);
  const [specimens, setSpecimens] = useState([]);
  const [groups, setGroups] = useState([]);
  const [organisms, setOrganisms] = useState([]);
  const [antimicrobials, setAntimicrobials] = useState([]);
  const [astMethods, setAstMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  // Active panel being edited or created
  const [selectedPanelId, setSelectedPanelId] = useState(null);
  const [panelForm, setPanelForm] = useState({
    panelID: null,
    panelCode: '',
    panelName: '',
    panelDescription: '',
    specimenID: '',
    organismGroupID: '',
    organismID: '',
    astMethodID: 1,
    status: 'Active',
    isActive: true,
    items: []
  });

  // Track unsaved dirty state
  const [isDirty, setIsDirty] = useState(false);
  const [pendingPanelSwitch, setPendingPanelSwitch] = useState(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  // Search & Filter controls
  const [panelSearchQuery, setPanelSearchQuery] = useState('');
  const [drugFilterSearch, setDrugFilterSearch] = useState('');
  const [selectedClassFilter, setSelectedClassFilter] = useState('ALL');

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    panelCode: '',
    panelName: '',
    panelDescription: '',
    specimenID: '',
    organismGroupID: '',
    organismID: '',
    astMethodID: 1,
    status: 'Draft',
    defaultReportingTier: 'Primary'
  });
  const [createFormErrors, setCreateFormErrors] = useState({});

  // Remove confirmation modal
  const [drugToRemove, setDrugToRemove] = useState(null);

  // Notification / Alert toast
  const [alertInfo, setAlertInfo] = useState({ show: false, type: 'success', message: '' });

  const showAlert = (message, type = 'success') => {
    setAlertInfo({ show: true, type, message });
    setTimeout(() => {
      setAlertInfo({ show: false, type: 'success', message: '' });
    }, 4500);
  };

  const loadData = async (preferredSelectId = null) => {
    try {
      setLoading(true);
      const [pRes, spRes, grpRes, orgRes, drugRes, mRes] = await Promise.all([
        microbiologyService.getASTPanels(),
        microbiologyService.getSpecimens(),
        microbiologyService.getOrganismGroups(),
        microbiologyService.getOrganisms(),
        microbiologyService.getAntimicrobials(),
        microbiologyService.getASTMethods()
      ]);

      const panelsData = pRes?.data || [];
      setPanels(panelsData);
      setSpecimens(spRes?.data || []);
      setGroups(grpRes?.data || []);
      setOrganisms(orgRes?.data || []);
      setAntimicrobials(drugRes?.data || []);
      setAstMethods(mRes?.data || []);

      if (preferredSelectId) {
        const found = panelsData.find((p) => p.panelID === preferredSelectId);
        if (found) {
          selectPanelInternal(found);
          return;
        }
      }

      if (panelsData.length > 0) {
        selectPanelInternal(panelsData[0]);
      } else {
        initBlankPanel();
      }
    } catch (err) {
      console.error('Error loading panel builder data:', err);
      showAlert('Failed to load AST panel data. Please retry.', 'error');
    } finally {
      setLoading(false);
      setIsDirty(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const selectPanelInternal = (panel) => {
    setSelectedPanelId(panel.panelID || null);
    setPanelForm({
      panelID: panel.panelID || null,
      panelCode: panel.panelCode || '',
      panelName: panel.panelName || '',
      panelDescription: panel.remarks || panel.panelDescription || '',
      specimenID: panel.specimenID || '',
      organismGroupID: panel.organismGroupID || '',
      organismID: panel.organismID || '',
      astMethodID: panel.astMethodID || astMethods[0]?.astMethodID || 1,
      status: panel.isActive ? 'Active' : 'Inactive',
      isActive: panel.isActive !== false,
      items: (panel.items || []).map((item, idx) => ({
        antimicrobialID: item.antimicrobialID,
        drugCode: item.drugCode,
        drugName: item.drugName,
        drugGroup: item.drugGroup || item.className || 'Standard',
        className: item.className || item.drugGroup || '',
        reportingCategory: item.reportingCategory || 'Mandatory',
        displayOrder: item.displayOrder || idx + 1,
        isDefault: item.isDefault ?? true,
        notes: item.notes || ''
      }))
    });
    setIsDirty(false);
  };

  const handlePanelSelectWithGuard = (panel) => {
    if (panel.panelID === selectedPanelId) return;
    if (isDirty) {
      setPendingPanelSwitch(panel);
      setShowUnsavedModal(true);
    } else {
      selectPanelInternal(panel);
    }
  };

  const initBlankPanel = () => {
    setSelectedPanelId(null);
    setPanelForm({
      panelID: null,
      panelCode: `PANEL_${Date.now().toString().slice(-4)}`,
      panelName: 'New AST Susceptibility Panel',
      panelDescription: '',
      specimenID: '',
      organismGroupID: groups[0]?.organismGroupID || '',
      organismID: '',
      astMethodID: astMethods[0]?.astMethodID || 1,
      status: 'Draft',
      isActive: false,
      items: []
    });
    setIsDirty(false);
  };

  // Open "Create New AST Panel" modal
  const handleOpenCreateModal = () => {
    setCreateForm({
      panelCode: `PANEL_${Date.now().toString().slice(-4)}`,
      panelName: '',
      panelDescription: '',
      specimenID: specimens[0]?.specimenID || '',
      organismGroupID: groups[0]?.organismGroupID || '',
      organismID: '',
      astMethodID: astMethods[0]?.astMethodID || 1,
      status: 'Draft',
      defaultReportingTier: 'Primary'
    });
    setCreateFormErrors({});
    setIsCreateModalOpen(true);
  };

  const validateCreateForm = () => {
    const errors = {};
    if (!createForm.panelCode.trim()) errors.panelCode = 'Panel Code is required';
    if (!createForm.panelName.trim()) errors.panelName = 'Panel Name is required';
    if (!createForm.specimenID) errors.specimenID = 'Specimen Matrix is required';
    if (!createForm.organismGroupID) errors.organismGroupID = 'Applicable Organism Group is required';
    if (!createForm.astMethodID) errors.astMethodID = 'Default AST Method is required';

    setCreateFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveNewPanelFromModal = async (asActive = false) => {
    if (!validateCreateForm()) return;

    const newStatus = asActive ? 'Active' : (createForm.status || 'Draft');
    const newPanel = {
      panelID: null,
      panelCode: createForm.panelCode.trim().toUpperCase(),
      panelName: createForm.panelName.trim(),
      panelDescription: createForm.panelDescription.trim(),
      remarks: createForm.panelDescription.trim(),
      specimenID: createForm.specimenID ? parseInt(createForm.specimenID) : null,
      organismGroupID: createForm.organismGroupID ? parseInt(createForm.organismGroupID) : null,
      organismID: createForm.organismID ? parseInt(createForm.organismID) : null,
      astMethodID: parseInt(createForm.astMethodID),
      status: newStatus,
      isActive: newStatus === 'Active',
      items: []
    };

    try {
      setSaveLoading(true);
      // Save directly to backend to register new panel
      const res = await microbiologyService.saveASTPanel({
        panelCode: newPanel.panelCode,
        panelName: newPanel.panelName,
        specimenID: newPanel.specimenID,
        organismGroupID: newPanel.organismGroupID,
        organismID: newPanel.organismID,
        astMethodID: newPanel.astMethodID,
        remarks: newPanel.remarks,
        isActive: newPanel.isActive,
        items: []
      });

      setIsCreateModalOpen(false);
      showAlert(`AST Panel "${newPanel.panelName}" created successfully! Add antimicrobials below.`, 'success');
      
      const createdId = res?.data?.panelID || res?.data?.id;
      await loadData(createdId);
    } catch (err) {
      console.error('Error creating new AST panel:', err);
      showAlert(err.response?.data?.message || 'Failed to create panel on the server — nothing was saved. Please retry.', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  // Duplicate Panel Workflow
  const handleDuplicatePanel = (panelToDuplicate) => {
    const p = panelToDuplicate || panelForm;
    const duplicatedPanel = {
      panelID: null,
      panelCode: `${p.panelCode || 'PANEL'}_COPY`.slice(0, 30),
      panelName: `${p.panelName || 'AST Panel'} (Copy)`,
      panelDescription: p.remarks || p.panelDescription || '',
      specimenID: p.specimenID || '',
      organismGroupID: p.organismGroupID || '',
      organismID: p.organismID || '',
      astMethodID: p.astMethodID || 1,
      status: 'Draft',
      isActive: false,
      items: (p.items || []).map((it, idx) => ({
        ...it,
        displayOrder: idx + 1
      }))
    };

    setSelectedPanelId(null);
    setPanelForm(duplicatedPanel);
    setIsDirty(true);
    showAlert(`Duplicated "${p.panelName}" as Draft. Review and save below.`, 'success');
  };

  // Drug toggle in mapping studio
  const toggleDrugSelection = (drug) => {
    const exists = panelForm.items.some((i) => i.antimicrobialID === drug.antimicrobialID);
    if (exists) {
      setDrugToRemove(drug);
    } else {
      const newItem = {
        antimicrobialID: drug.antimicrobialID,
        drugCode: drug.drugCode,
        drugName: drug.drugName,
        drugGroup: drug.drugGroup || drug.className || 'Standard',
        className: drug.className || drug.drugGroup || '',
        reportingCategory: 'Mandatory',
        displayOrder: panelForm.items.length + 1,
        isDefault: true,
        notes: ''
      };
      setPanelForm({
        ...panelForm,
        items: [...panelForm.items, newItem]
      });
      setIsDirty(true);
    }
  };

  const confirmRemoveDrug = () => {
    if (!drugToRemove) return;
    const remaining = panelForm.items
      .filter((i) => i.antimicrobialID !== drugToRemove.antimicrobialID)
      .map((item, idx) => ({ ...item, displayOrder: idx + 1 }));

    setPanelForm({ ...panelForm, items: remaining });
    setDrugToRemove(null);
    setIsDirty(true);
  };

  const updateItemCategory = (antimicrobialID, category) => {
    setPanelForm({
      ...panelForm,
      items: panelForm.items.map((i) =>
        i.antimicrobialID === antimicrobialID ? { ...i, reportingCategory: category } : i
      )
    });
    setIsDirty(true);
  };

  const moveItemOrder = (index, direction) => {
    const items = [...panelForm.items];
    const targetIdx = index + direction;
    if (targetIdx < 0 || targetIdx >= items.length) return;

    const temp = items[index];
    items[index] = items[targetIdx];
    items[targetIdx] = temp;

    // Renumber
    items.forEach((item, idx) => {
      item.displayOrder = idx + 1;
    });

    setPanelForm({ ...panelForm, items });
    setIsDirty(true);
  };

  // Form saving (Draft vs Active)
  const handleSavePanel = async (asActive = false) => {
    if (!panelForm.panelName.trim() || !panelForm.panelCode.trim()) {
      showAlert('Panel Code and Panel Name are mandatory fields.', 'error');
      return;
    }

    if (asActive) {
      if (!panelForm.specimenID) {
        showAlert('Specimen Matrix is required before activating panel.', 'error');
        return;
      }
      if (!panelForm.organismGroupID) {
        showAlert('Applicable Organism Group is required before activating panel.', 'error');
        return;
      }
      if (panelForm.items.length === 0) {
        showAlert('At least one antimicrobial must be configured in an Active AST Panel.', 'error');
        return;
      }
    }

    try {
      setSaveLoading(true);
      const targetIsActive = asActive ? true : (panelForm.status === 'Active');
      const payload = {
        panelID: panelForm.panelID,
        panelCode: panelForm.panelCode.trim().toUpperCase(),
        panelName: panelForm.panelName.trim(),
        remarks: panelForm.panelDescription || '',
        specimenID: panelForm.specimenID ? parseInt(panelForm.specimenID) : null,
        organismGroupID: panelForm.organismGroupID ? parseInt(panelForm.organismGroupID) : null,
        organismID: panelForm.organismID ? parseInt(panelForm.organismID) : null,
        astMethodID: parseInt(panelForm.astMethodID) || 1,
        isActive: targetIsActive,
        items: panelForm.items.map((it, idx) => ({
          antimicrobialID: it.antimicrobialID,
          reportingCategory: it.reportingCategory || 'Mandatory',
          displayOrder: idx + 1,
          isDefault: it.isDefault ?? true,
          notes: it.notes || ''
        }))
      };

      const res = await microbiologyService.saveASTPanel(payload);
      const savedId = res?.data?.panelID || panelForm.panelID;
      showAlert(`AST Panel "${panelForm.panelName}" ${asActive ? 'activated' : 'saved'} successfully!`, 'success');
      setIsDirty(false);
      await loadData(savedId);
    } catch (err) {
      console.error('Error saving AST panel:', err);
      showAlert(err.response?.data?.message || 'Error saving AST Panel configuration.', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  // Filtered panel list on left side
  const filteredPanels = useMemo(() => {
    return panels.filter((p) => {
      if (!panelSearchQuery) return true;
      const q = panelSearchQuery.toLowerCase();
      return (
        p.panelName?.toLowerCase().includes(q) ||
        p.panelCode?.toLowerCase().includes(q) ||
        p.specimenName?.toLowerCase().includes(q) ||
        p.groupName?.toLowerCase().includes(q)
      );
    });
  }, [panels, panelSearchQuery]);

  // Centralized Standardized Pagination for Panel Library
  const panelPagination = usePagination({
    items: filteredPanels,
    initialPage: 1,
    initialPageSize: 10,
    resetDeps: [panelSearchQuery]
  });

  // Drug classes for filter pills/dropdown
  const uniqueDrugClasses = useMemo(() => {
    const set = new Set();
    antimicrobials.forEach((d) => {
      if (d.className) set.add(d.className);
      else if (d.drugGroup) set.add(d.drugGroup);
    });
    return Array.from(set);
  }, [antimicrobials]);

  // Filtered antimicrobial library on right side
  const filteredDrugs = useMemo(() => {
    return antimicrobials.filter((d) => {
      const matchesSearch = !drugFilterSearch ||
        d.drugName?.toLowerCase().includes(drugFilterSearch.toLowerCase()) ||
        d.drugCode?.toLowerCase().includes(drugFilterSearch.toLowerCase()) ||
        (d.className && d.className.toLowerCase().includes(drugFilterSearch.toLowerCase())) ||
        (d.shortName && d.shortName.toLowerCase().includes(drugFilterSearch.toLowerCase()));

      const matchesClass = selectedClassFilter === 'ALL' ||
        d.className === selectedClassFilter ||
        d.drugGroup === selectedClassFilter;

      return matchesSearch && matchesClass;
    });
  }, [antimicrobials, drugFilterSearch, selectedClassFilter]);

  // Active panel stats
  const activeCount = panels.filter((p) => p.isActive).length;
  const draftCount = panels.filter((p) => !p.isActive).length;

  // Selected panel helper names
  const selectedSpecimenName = specimens.find((s) => s.specimenID === parseInt(panelForm.specimenID))?.specimenName || 'All Specimen Matrices';
  const selectedGroupName = groups.find((g) => g.organismGroupID === parseInt(panelForm.organismGroupID))?.groupName || 'All Organism Groups';
  const selectedOrgName = organisms.find((o) => o.organismID === parseInt(panelForm.organismID))?.organismName;
  const selectedMethodName = astMethods.find((m) => m.astMethodID === parseInt(panelForm.astMethodID))?.methodName || 'Kirby-Bauer Disk Diffusion';

  return (
    <div className="micro-master-container">
      {/* Toast Notification Alert */}
      {alertInfo.show && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '24px',
            zIndex: 9999,
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)',
            background: alertInfo.type === 'error' ? '#ef4444' : '#10b981',
            color: '#ffffff',
            fontWeight: '600',
            fontSize: '0.88rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          <span>{alertInfo.type === 'error' ? '⚠️' : '✓'}</span>
          <span>{alertInfo.message}</span>
        </div>
      )}

      {/* Main Page Header */}
      <div className="micro-header-section">
        <div className="micro-header-title">
          <h1>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18M9 21V9" />
            </svg>
            AST Panel Builder & Antibiotic Mapping Studio
          </h1>
          <p>Configure dynamic antimicrobial susceptibility panels by Specimen Matrix, Organism Group, and Selective Reporting tiers</p>
        </div>
        <div className="micro-header-actions">
          <button
            type="button"
            className="micro-btn-secondary"
            onClick={() => handleDuplicatePanel()}
            title="Duplicate current AST panel into a draft copy"
          >
            📋 Duplicate Panel
          </button>
          <button
            type="button"
            className="micro-btn-primary"
            onClick={handleOpenCreateModal}
            id="btn-create-ast-panel"
          >
            + Create New AST Panel
          </button>
        </div>
      </div>

      {/* 2-Column Responsive Layout */}
      <div className="panel-builder-layout">
        {/* ========================================================= */}
        {/* LEFT COLUMN: Panel Library & Selected Panel Configuration */}
        {/* ========================================================= */}
        <div className="panel-meta-card">
          {/* Header with Panel Counts */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#0f172a' }}>
                Configured AST Panels
              </h3>
              <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
                {panels.length} Total Panels &bull; <span style={{ color: '#16a34a', fontWeight: '600' }}>{activeCount} Active</span> &bull; <span style={{ color: '#d97706', fontWeight: '600' }}>{draftCount} Draft</span>
              </div>
            </div>
            {isDirty && (
              <span className="micro-badge" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.72rem' }}>
                Unsaved Edits
              </span>
            )}
          </div>

          {/* Search Box for Panels */}
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search panels by name, code..."
              value={panelSearchQuery}
              onChange={(e) => setPanelSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '7px 10px 7px 30px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '0.82rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            <span style={{ position: 'absolute', left: '9px', top: '7px', color: '#94a3b8', fontSize: '0.85rem' }}>
              🔍
            </span>
          </div>

          {/* Selectable Panel List */}
          <div className="ast-panel-list-container">
            {loading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '0.84rem' }}>
                Loading panels...
              </div>
            ) : filteredPanels.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '0.84rem' }}>
                No panels found. Click "+ Create New AST Panel" above.
              </div>
            ) : (
              panelPagination.pagedItems.map((p) => {
                const isSelected = selectedPanelId === p.panelID;
                return (
                  <div
                    key={p.panelID}
                    className={`ast-panel-item-card ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => handlePanelSelectWithGuard(p)}
                  >
                    <div className="ast-panel-item-header">
                      <div className="ast-panel-item-title">{p.panelName}</div>
                      <span className="ast-panel-item-code">{p.panelCode}</span>
                    </div>
                    <div className="ast-panel-item-meta">
                      <div>📁 {p.specimenName || 'All Specimen Matrices'}</div>
                      <div>🦠 {p.groupName || 'All Organisms'}{p.organismName ? ` (${p.organismName})` : ''}</div>
                    </div>
                    <div className="ast-panel-item-footer">
                      <span
                        className={`ast-status-badge ${
                          p.isActive ? 'status-active' : 'status-draft'
                        }`}
                      >
                        <span className="micro-status-dot" style={{ background: p.isActive ? '#16a34a' : '#d97706' }} />
                        {p.isActive ? 'Active' : 'Draft'}
                      </span>

                      <div className="ast-panel-action-btns" onClick={(e) => e.stopPropagation()}>
                        <span className="micro-badge" style={{ background: '#ede9fe', color: '#6d28d9', fontSize: '0.72rem' }}>
                          {p.items?.length || 0} drugs
                        </span>
                        <button
                          type="button"
                          className="ast-action-icon-btn"
                          title="Duplicate this panel"
                          onClick={() => handleDuplicatePanel(p)}
                        >
                          📋 Copy
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Centralized Standard LIMS Pagination for Panels */}
          <CommonPagination
            {...panelPagination.paginationProps}
            loading={loading}
          />

          <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '4px 0' }} />

          {/* Panel Configuration Editor Form */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, fontSize: '0.92rem', fontWeight: '700', color: '#0f172a' }}>
                Panel Configuration
              </h4>
              <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
                {selectedPanelId ? `ID #${selectedPanelId}` : 'New Unsaved Panel'}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div className="micro-form-group">
                <label>
                  Panel Code <span className="req-star">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. PANEL_BLOOD_STAPH"
                  value={panelForm.panelCode}
                  onChange={(e) => {
                    setPanelForm({ ...panelForm, panelCode: e.target.value.toUpperCase() });
                    setIsDirty(true);
                  }}
                  style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontWeight: '600' }}
                />
              </div>

              <div className="micro-form-group">
                <label>
                  Panel Name <span className="req-star">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Blood Staphylococcal Susceptibility Panel"
                  value={panelForm.panelName}
                  onChange={(e) => {
                    setPanelForm({ ...panelForm, panelName: e.target.value });
                    setIsDirty(true);
                  }}
                />
              </div>

              <div className="micro-form-group">
                <label>Panel Description</label>
                <textarea
                  rows={2}
                  placeholder="e.g. Routine blood culture AST panel for clinically significant Staphylococcus isolates."
                  value={panelForm.panelDescription}
                  onChange={(e) => {
                    setPanelForm({ ...panelForm, panelDescription: e.target.value });
                    setIsDirty(true);
                  }}
                />
              </div>

              <div className="micro-form-group">
                <label>
                  Specimen Matrix <span className="req-star">*</span>
                </label>
                <select
                  value={panelForm.specimenID}
                  onChange={(e) => {
                    setPanelForm({ ...panelForm, specimenID: e.target.value });
                    setIsDirty(true);
                  }}
                >
                  <option value="">-- All Specimen Matrices --</option>
                  {specimens.map((sp) => (
                    <option key={sp.specimenID} value={sp.specimenID}>
                      {sp.specimenName} ({sp.specimenGroup || 'General'})
                    </option>
                  ))}
                </select>
              </div>

              <div className="micro-form-group">
                <label>
                  Applicable Organism Group <span className="req-star">*</span>
                </label>
                <select
                  value={panelForm.organismGroupID}
                  onChange={(e) => {
                    setPanelForm({ ...panelForm, organismGroupID: e.target.value });
                    setIsDirty(true);
                  }}
                >
                  <option value="">-- Applicable to ALL Groups --</option>
                  {groups.map((g) => (
                    <option key={g.organismGroupID} value={g.organismGroupID}>
                      {g.groupName} ({g.gramType || 'All'})
                    </option>
                  ))}
                </select>
              </div>

              <div className="micro-form-group">
                <label>Specific Organism (Optional Override)</label>
                <select
                  value={panelForm.organismID}
                  onChange={(e) => {
                    setPanelForm({ ...panelForm, organismID: e.target.value });
                    setIsDirty(true);
                  }}
                >
                  <option value="">-- No specific override (Group level) --</option>
                  {organisms.map((o) => (
                    <option key={o.organismID} value={o.organismID}>
                      {o.organismName}
                    </option>
                  ))}
                </select>
                <span className="micro-form-helper">
                  Specific organism overrides group-level configuration.
                </span>
              </div>

              <div className="micro-form-group">
                <label>
                  Default AST Method <span className="req-star">*</span>
                </label>
                <select
                  value={panelForm.astMethodID}
                  onChange={(e) => {
                    setPanelForm({ ...panelForm, astMethodID: e.target.value });
                    setIsDirty(true);
                  }}
                >
                  {astMethods.map((m) => (
                    <option key={m.astMethodID} value={m.astMethodID}>
                      {m.methodName} ({m.measuredUnit})
                    </option>
                  ))}
                </select>
              </div>

              <div className="micro-form-group">
                <label>Status</label>
                <div className="micro-status-group">
                  {['Active', 'Draft', 'Inactive', 'Deprecated'].map((st) => (
                    <button
                      key={st}
                      type="button"
                      className={`micro-status-btn ${
                        panelForm.status === st ? `active-status-${st.toLowerCase()}` : ''
                      }`}
                      onClick={() => {
                        setPanelForm({ ...panelForm, status: st, isActive: st === 'Active' });
                        setIsDirty(true);
                      }}
                    >
                      {st}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                <button
                  type="button"
                  className="micro-btn-secondary"
                  style={{ flex: 1, justifyContent: 'center' }}
                  onClick={() => handleSavePanel(false)}
                  disabled={saveLoading}
                >
                  Save Draft
                </button>
                <button
                  type="button"
                  className="micro-btn-primary"
                  style={{ flex: 1.3, justifyContent: 'center' }}
                  onClick={() => handleSavePanel(true)}
                  disabled={saveLoading}
                >
                  {saveLoading ? 'Saving...' : 'Save & Activate'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ========================================================= */}
        {/* RIGHT COLUMN: Active Panel Summary & Antimicrobial Studio */}
        {/* ========================================================= */}
        <div className="panel-items-card">
          {/* Top Context Summary Banner */}
          <div className="micro-summary-bar">
            <div className="micro-summary-item">
              <strong>Panel:</strong> {panelForm.panelName || 'Untitled Panel'}
            </div>
            <span className="micro-summary-divider">|</span>
            <div className="micro-summary-item">
              <strong>Code:</strong> <code style={{ color: '#0369a1' }}>{panelForm.panelCode || 'N/A'}</code>
            </div>
            <span className="micro-summary-divider">|</span>
            <div className="micro-summary-item">
              <strong>Specimen:</strong> {selectedSpecimenName}
            </div>
            <span className="micro-summary-divider">|</span>
            <div className="micro-summary-item">
              <strong>Target:</strong> {selectedOrgName || selectedGroupName}
            </div>
            <span className="micro-summary-divider">|</span>
            <div className="micro-summary-item">
              <strong>Method:</strong> {selectedMethodName}
            </div>
            <span className="micro-summary-divider">|</span>
            <div className="micro-summary-item">
              <span className={`ast-status-badge ${panelForm.isActive ? 'status-active' : 'status-draft'}`}>
                {panelForm.isActive ? '● Active' : '○ Draft'}
              </span>
            </div>
          </div>

          {/* Section Title & Subtitle */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#0f172a' }}>
                Antimicrobial Mapping
              </h3>
              <p style={{ margin: '3px 0 0 0', fontSize: '0.82rem', color: '#64748b' }}>
                Select antimicrobials and configure their reporting order and reporting category ({panelForm.items.length} drugs configured)
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                className="micro-btn-secondary"
                style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                onClick={() => handleSavePanel(false)}
              >
                Save Draft
              </button>
              <button
                type="button"
                className="micro-btn-primary"
                style={{ padding: '6px 14px', fontSize: '0.8rem' }}
                onClick={() => handleSavePanel(true)}
              >
                Save & Activate
              </button>
            </div>
          </div>

          {/* Antimicrobial Search & Selector Studio */}
          <div className="ast-drug-picker-box">
            <div className="ast-drug-picker-header">
              <span style={{ fontSize: '0.84rem', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>💊</span> Antimicrobial Drug Library:
              </span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Class Filter */}
                <select
                  value={selectedClassFilter}
                  onChange={(e) => setSelectedClassFilter(e.target.value)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.8rem',
                    background: '#ffffff'
                  }}
                >
                  <option value="ALL">All Drug Classes</option>
                  {uniqueDrugClasses.map((cls) => (
                    <option key={cls} value={cls}>
                      {cls}
                    </option>
                  ))}
                </select>

                {/* Search Box */}
                <input
                  type="text"
                  placeholder="Search antimicrobials..."
                  value={drugFilterSearch}
                  onChange={(e) => setDrugFilterSearch(e.target.value)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.8rem',
                    width: '210px'
                  }}
                />
              </div>
            </div>

            {/* Quick Add / Remove Chips */}
            <div className="drug-pill-selection">
              {filteredDrugs.length === 0 ? (
                <div style={{ padding: '8px', color: '#94a3b8', fontSize: '0.8rem' }}>
                  No antimicrobials match the filter criteria.
                </div>
              ) : (
                filteredDrugs.map((drug) => {
                  const isSelected = panelForm.items.some((i) => i.antimicrobialID === drug.antimicrobialID);
                  return (
                    <span
                      key={drug.antimicrobialID}
                      className={`drug-pill ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleDrugSelection(drug)}
                      title={`${drug.drugName} (${drug.drugCode}) - ${drug.className || 'Antimicrobial'}`}
                    >
                      <span>{isSelected ? '✓' : '+'}</span>
                      <span>{drug.drugName}</span>
                      <small style={{ opacity: 0.8, fontSize: '0.7rem' }}>[{drug.drugCode}]</small>
                    </span>
                  );
                })
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', fontSize: '0.74rem', color: '#64748b' }}>
              <span>Showing {filteredDrugs.length} of {antimicrobials.length} available antimicrobials</span>
              <span>Click a drug chip to add/remove from panel mapping</span>
            </div>
          </div>

          {/* Configured Panel Drugs Table */}
          <div className="micro-table-wrapper">
            <table className="micro-table">
              <thead>
                <tr>
                  <th style={{ width: '60px', textAlign: 'center' }}>Order</th>
                  <th style={{ width: '120px' }}>Drug Code</th>
                  <th>Antimicrobial Name</th>
                  <th>Pharmacological Class</th>
                  <th style={{ width: '220px' }}>Reporting Category</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>Reorder</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>Remove</th>
                </tr>
              </thead>
              <tbody>
                {panelForm.items.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                      <div style={{ fontSize: '1.8rem', marginBottom: '8px' }}>🧪</div>
                      <div style={{ fontWeight: '600', color: '#334155', marginBottom: '4px' }}>
                        No antimicrobials configured
                      </div>
                      <div style={{ fontSize: '0.82rem', color: '#94a3b8', maxWidth: '420px', margin: '0 auto' }}>
                        Search the antimicrobial library above and click on the drugs required for this AST panel.
                      </div>
                    </td>
                  </tr>
                ) : (
                  panelForm.items.map((item, index) => (
                    <tr key={item.antimicrobialID}>
                      <td style={{ textAlign: 'center' }}>
                        <span className="ast-panel-item-code" style={{ fontSize: '0.8rem', fontWeight: '700' }}>
                          #{item.displayOrder}
                        </span>
                      </td>
                      <td>
                        <strong style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', color: '#0369a1' }}>
                          {item.drugCode}
                        </strong>
                      </td>
                      <td>
                        <strong style={{ color: '#0f172a' }}>{item.drugName}</strong>
                      </td>
                      <td>
                        <span className="micro-badge" style={{ background: '#f1f5f9', color: '#475569' }}>
                          {item.className || item.drugGroup || 'Standard'}
                        </span>
                      </td>
                      <td>
                        <select
                          className="micro-filter-select"
                          style={{ padding: '5px 8px', fontSize: '0.82rem', width: '100%', fontWeight: '500' }}
                          value={item.reportingCategory}
                          onChange={(e) => updateItemCategory(item.antimicrobialID, e.target.value)}
                        >
                          <option value="Mandatory">Primary (Mandatory / Routine)</option>
                          <option value="Optional">Secondary (Selective Reporting)</option>
                          <option value="Conditional">Extended (Conditional on R)</option>
                          <option value="Suppressed">Surveillance (Hospital Internal)</option>
                        </select>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', gap: '4px' }}>
                          <button
                            type="button"
                            className="micro-btn-secondary"
                            style={{ padding: '3px 7px', fontSize: '0.74rem' }}
                            disabled={index === 0}
                            title="Change reporting order: Move up"
                            onClick={() => moveItemOrder(index, -1)}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="micro-btn-secondary"
                            style={{ padding: '3px 7px', fontSize: '0.74rem' }}
                            disabled={index === panelForm.items.length - 1}
                            title="Change reporting order: Move down"
                            onClick={() => moveItemOrder(index, 1)}
                          >
                            ▼
                          </button>
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="ast-action-icon-btn"
                          style={{ color: '#ef4444', borderColor: '#fca5a5' }}
                          title={`Remove ${item.drugName} from panel`}
                          onClick={() => setDrugToRemove(item)}
                        >
                          🗑 Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ========================================================= */}
      {/* MODAL: CREATE NEW AST PANEL                               */}
      {/* ========================================================= */}
      {isCreateModalOpen && (
        <div className="micro-modal-overlay">
          <div className="micro-modal-content" style={{ maxWidth: '780px' }}>
            {/* Modal Header */}
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18M9 21V9" />
                  </svg>
                  Create New AST Panel
                </h3>
                <p>Configure an antimicrobial susceptibility testing panel for a defined specimen and organism group.</p>
              </div>
              <button
                type="button"
                className="micro-modal-close-btn"
                onClick={() => setIsCreateModalOpen(false)}
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div className="micro-modal-body">
              {/* Section 1: Panel Identity */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>1. Panel Identity</span>
                  <span className="section-badge">Required</span>
                </div>
                <div className="micro-form-grid-2">
                  <div className="micro-form-group">
                    <label>
                      Panel Code <span className="req-star">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. PANEL_BLOOD_GNB"
                      value={createForm.panelCode}
                      className={createFormErrors.panelCode ? 'is-invalid' : ''}
                      onChange={(e) => {
                        setCreateForm({ ...createForm, panelCode: e.target.value.toUpperCase() });
                        setCreateFormErrors({ ...createFormErrors, panelCode: null });
                      }}
                      style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontWeight: '600' }}
                    />
                    {createFormErrors.panelCode && (
                      <span className="micro-form-error">{createFormErrors.panelCode}</span>
                    )}
                  </div>

                  <div className="micro-form-group">
                    <label>
                      Panel Name <span className="req-star">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Blood Gram-Negative Bacilli Susceptibility Panel"
                      value={createForm.panelName}
                      className={createFormErrors.panelName ? 'is-invalid' : ''}
                      onChange={(e) => {
                        setCreateForm({ ...createForm, panelName: e.target.value });
                        setCreateFormErrors({ ...createFormErrors, panelName: null });
                      }}
                    />
                    {createFormErrors.panelName && (
                      <span className="micro-form-error">{createFormErrors.panelName}</span>
                    )}
                  </div>
                </div>

                <div className="micro-form-group" style={{ marginTop: '12px' }}>
                  <label>Panel Description</label>
                  <textarea
                    rows={2}
                    placeholder="e.g. Routine blood culture AST panel for clinically significant Enterobacterales isolates."
                    value={createForm.panelDescription}
                    onChange={(e) => setCreateForm({ ...createForm, panelDescription: e.target.value })}
                  />
                  <span className="micro-form-helper">Optional clinical summary of this panel's target scope and utility.</span>
                </div>

                <div className="micro-form-group" style={{ marginTop: '12px' }}>
                  <label>Status <span className="req-star">*</span></label>
                  <div className="micro-status-group">
                    {['Draft', 'Active', 'Inactive', 'Deprecated'].map((st) => (
                      <button
                        key={st}
                        type="button"
                        className={`micro-status-btn ${
                          createForm.status === st ? `active-status-${st.toLowerCase()}` : ''
                        }`}
                        onClick={() => setCreateForm({ ...createForm, status: st })}
                      >
                        {st}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Section 2: Clinical Scope */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>2. Clinical Scope & AST Method</span>
                  <span className="section-badge">Matrix Matching</span>
                </div>

                <div className="micro-form-grid-2">
                  <div className="micro-form-group">
                    <label>
                      Specimen Matrix <span className="req-star">*</span>
                    </label>
                    <select
                      value={createForm.specimenID}
                      className={createFormErrors.specimenID ? 'is-invalid' : ''}
                      onChange={(e) => {
                        setCreateForm({ ...createForm, specimenID: e.target.value });
                        setCreateFormErrors({ ...createFormErrors, specimenID: null });
                      }}
                    >
                      <option value="">-- Select Specimen Matrix --</option>
                      {specimens.map((sp) => (
                        <option key={sp.specimenID} value={sp.specimenID}>
                          {sp.specimenName} ({sp.specimenGroup || 'General'})
                        </option>
                      ))}
                    </select>
                    {createFormErrors.specimenID && (
                      <span className="micro-form-error">{createFormErrors.specimenID}</span>
                    )}
                  </div>

                  <div className="micro-form-group">
                    <label>
                      Applicable Organism Group <span className="req-star">*</span>
                    </label>
                    <select
                      value={createForm.organismGroupID}
                      className={createFormErrors.organismGroupID ? 'is-invalid' : ''}
                      onChange={(e) => {
                        setCreateForm({ ...createForm, organismGroupID: e.target.value });
                        setCreateFormErrors({ ...createFormErrors, organismGroupID: null });
                      }}
                    >
                      <option value="">-- Select Organism Group --</option>
                      {groups.map((g) => (
                        <option key={g.organismGroupID} value={g.organismGroupID}>
                          {g.groupName} ({g.gramType || 'All'})
                        </option>
                      ))}
                    </select>
                    {createFormErrors.organismGroupID && (
                      <span className="micro-form-error">{createFormErrors.organismGroupID}</span>
                    )}
                  </div>
                </div>

                <div className="micro-form-grid-2" style={{ marginTop: '12px' }}>
                  <div className="micro-form-group">
                    <label>Specific Organism Override (Optional)</label>
                    <select
                      value={createForm.organismID}
                      onChange={(e) => setCreateForm({ ...createForm, organismID: e.target.value })}
                    >
                      <option value="">-- No specific override (Group level) --</option>
                      {organisms.map((o) => (
                        <option key={o.organismID} value={o.organismID}>
                          {o.organismName}
                        </option>
                      ))}
                    </select>
                    <span className="micro-form-helper">
                      Specific organism overrides group-level configuration.
                    </span>
                  </div>

                  <div className="micro-form-group">
                    <label>
                      Default AST Method <span className="req-star">*</span>
                    </label>
                    <select
                      value={createForm.astMethodID}
                      className={createFormErrors.astMethodID ? 'is-invalid' : ''}
                      onChange={(e) => {
                        setCreateForm({ ...createForm, astMethodID: e.target.value });
                        setCreateFormErrors({ ...createFormErrors, astMethodID: null });
                      }}
                    >
                      {astMethods.map((m) => (
                        <option key={m.astMethodID} value={m.astMethodID}>
                          {m.methodName} ({m.measuredUnit})
                        </option>
                      ))}
                    </select>
                    {createFormErrors.astMethodID && (
                      <span className="micro-form-error">{createFormErrors.astMethodID}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="micro-modal-footer">
              <button
                type="button"
                className="micro-btn-secondary"
                onClick={() => setIsCreateModalOpen(false)}
              >
                Cancel
              </button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className="micro-btn-secondary"
                  onClick={() => handleSaveNewPanelFromModal(false)}
                  disabled={saveLoading}
                >
                  Save Draft
                </button>
                <button
                  type="button"
                  className="micro-btn-primary"
                  onClick={() => handleSaveNewPanelFromModal(true)}
                  disabled={saveLoading}
                >
                  {saveLoading ? 'Creating...' : 'Create & Configure Drugs'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL: CONFIRM REMOVE ANTIMICROBIAL                       */}
      {/* ========================================================= */}
      {drugToRemove && (
        <div className="micro-modal-overlay">
          <div className="micro-modal-content" style={{ maxWidth: '440px' }}>
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3 style={{ color: '#b91c1c' }}>Remove Antimicrobial</h3>
              </div>
              <button
                type="button"
                className="micro-modal-close-btn"
                onClick={() => setDrugToRemove(null)}
              >
                &times;
              </button>
            </div>
            <div className="micro-modal-body" style={{ padding: '20px 24px' }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#334155' }}>
                Remove <strong>{drugToRemove.drugName}</strong> ({drugToRemove.drugCode}) from this AST panel?
              </p>
            </div>
            <div className="micro-modal-footer">
              <button
                type="button"
                className="micro-btn-secondary"
                onClick={() => setDrugToRemove(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="micro-btn-primary"
                style={{ background: '#ef4444' }}
                onClick={confirmRemoveDrug}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL: UNSAVED CHANGES GUARD                              */}
      {/* ========================================================= */}
      {showUnsavedModal && (
        <div className="micro-modal-overlay">
          <div className="micro-modal-content" style={{ maxWidth: '440px' }}>
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3 style={{ color: '#d97706' }}>Unsaved Changes</h3>
              </div>
              <button
                type="button"
                className="micro-modal-close-btn"
                onClick={() => setShowUnsavedModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="micro-modal-body" style={{ padding: '20px 24px' }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#334155' }}>
                You have unsaved changes in the current AST panel. Do you want to discard your edits and switch panels?
              </p>
            </div>
            <div className="micro-modal-footer">
              <button
                type="button"
                className="micro-btn-secondary"
                onClick={() => setShowUnsavedModal(false)}
              >
                Stay
              </button>
              <button
                type="button"
                className="micro-btn-primary"
                style={{ background: '#d97706' }}
                onClick={() => {
                  setShowUnsavedModal(false);
                  if (pendingPanelSwitch) {
                    selectPanelInternal(pendingPanelSwitch);
                    setPendingPanelSwitch(null);
                  }
                }}
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
